import type {ReduxStore} from 'src/reducers';
import IndexStore from 'src/stores';

import {isInternalUser} from 'src/utils/check-internal-user';

import './app.global.css';

import styles from './app.css';
import logger from 'src/utils/logger';
import {getCurrentLanguageForApp} from 'src/utils/language-support';

import * as React from 'react';
import {DndProvider} from 'react-dnd-cjs';
import HTML5Backend from 'react-dnd-html5-backend-cjs';
import {Helmet} from 'react-helmet';
import {Provider} from 'react-redux';

import FluxRoot from 'src/flux/root';
import {isMobile, mobileWidthPx} from 'src/utils/responsive';
import {isDevOrStage} from 'src/utils/env';

import {WindowWidthProvider} from 'src/components/lib/responsive';
import AgencyConfigContext from 'src/contexts/agency-config';
import ErrorBoundary from 'src/components/500/boundary';
import DragLayer from 'src/components/lib/dnd/layer';
import ModalRoot from 'src/components/modals/modal-root';
import {ContextMenuRoot} from 'src/components/lib/context-menu';
import {MouseTipRoot} from 'src/components/lib/mouse-tip/mouse-tip';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {startUntranslatedLabelsPoller} from 'src/utils/startUntranslatedLabelsPoller';
import {isMissingTranslationsLoggingEnabled} from 'src/utils';

import {getCorporateLabels} from 'src/action-creators/language-support';
import labelI18nInstance, {
  ensureInitialized,
} from 'src/components/lib/i18n/new-i18n';
import I18nContext from 'src/contexts/i18n-context';
import ToastContainer from 'src/designSystem2021Components/toast/ToastContainer';
import {ToastContainer as GenesisToastContainer} from '@spaced-out/ui-design-system/lib/components/Toast';
import {PageTitleProvider} from 'src/contexts/page-title-context';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';


type Props = {
  reduxStore: ReduxStore;
  senseStore: IndexStore;
  children: React.ReactNode;
};

export default function App({senseStore, reduxStore, children}: Props) {
  const [isCorporateLabelsLoading, setIsCorporateLabelsLoading] =
    React.useState(true);
  const [i18nState, setI18nState] = React.useState(labelI18nInstance);
  const [i18nReady, setI18nReady] = React.useState(false);
  const {
    requestHeaders: {headers},
    agency: {config, agency},
    env,
    accounts,
  } = reduxStore.getState();

  // start the poller if the feature flag is enabled for dynamic label translation ui
  if (isMissingTranslationsLoggingEnabled()) {
    try {
      startUntranslatedLabelsPoller(reduxStore);
    } catch (e: any) {
      logger.error('Error starting untranslated labels poller', e);
    }
  } else {
    try {
      localStorage.removeItem('untranslatedLabels');
    } catch (e: any) {
      logger.error('Error removing untranslatedLabels from localStorage', e);
    }
  }

  const agent = accounts.authedUserId
    ? accounts.data[accounts.authedUserId]
    : null;
  const webPlatformContext =
    env.query.context === 'chrome-extension' ? 'Extension' : 'Web';
  const clarityPropertiesToTrack = {
    'Agency Id': agency?.id,
    'Agency Name': agency?.name,
    'Agent Id': agent?.id,
    // @ts-expect-error - TS2774 - This condition will always return true since this function is always defined. Did you mean to call it instead?
    'Internal User': isInternalUser ? 'Yes' : 'No',
    'ATS System': agency?.atsSystem,
    'Sense Web Platform context': webPlatformContext,
  } as const;

  const authedUserId = reduxStore.getState().accounts.authedUserId;
  const canduToken = reduxStore.getState().env.vendorIds.candu;
  const {i18NSelectedLanguage: agentI18NSelectedLanguage} =
    reduxStore.getState().agentSettings;

  const {
    i18NDefaultLanguage: agencyDefaultLanguage,
    i18NEnableLanguageSupport: isLanguageSupportEnabled,
  } = agency;
  // clean up localStorage if language support is disabled
  if (!isLanguageSupportEnabled) {
    try {
      localStorage.removeItem('selectedLanguage');
    } catch (e: any) {
      logger.error('Error removing selectedLanguage from localStorage', e);
    }
  }

  const finalResolvedLanguage = getCurrentLanguageForApp(
    isLanguageSupportEnabled,
    agentI18NSelectedLanguage,
    agencyDefaultLanguage,
    {supportLocalStorageLanguage: false},
  );

  React.useEffect(() => {
    window.document.addEventListener('dragover', handleDragOver);
    try {
      window?.chmln?.identify(agent?.id, {
        email: agent?.email,
        name: agent?.name,
        agencyId: agency?.id,
        agencyName: agency?.name,
        company: {uid: agency?.id, salesforce_uid: agency?.salesforceAccountId},
      });
    } catch (e: any) {
      logger.error('Exception in chameleon');
    }

    for (const [key, value] of Object.entries(clarityPropertiesToTrack)) {
      try {
        window?.clarity?.('set', key, value);
      } catch (e: any) {
        logger.error('clarity is not enabled');
      }
    }
    try {
      window?.Candu?.init({
        clientToken: canduToken,
        userId: authedUserId,
        traits: {
          email: agent?.email,
          name: agent?.name,
          agencyId: agency?.id,
          salesforceUid: agency?.salesforceAccountId,
        },
      });
    } catch (e: any) {
      logger.error('Exception in Candu', e);
    }

    return () => {
      window.document.removeEventListener('dragover', handleDragOver);
    };
  }, []);

  React.useEffect(() => {
    const initializeI18n = async () => {
      try {
        await ensureInitialized(finalResolvedLanguage); // ensure translations are initialized
        setI18nReady(true);
      } catch (error: any) {
        console.error('Error initializing translations:', error);
        setI18nReady(true); // allow app to proceed even if translations fail
      }
    };

    initializeI18n();

    const handleLanguageChanged = () => {
      setI18nState({...labelI18nInstance});
    };

    labelI18nInstance.on('languageChanged', handleLanguageChanged);

    return () => {
      labelI18nInstance.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  React.useEffect(() => {
    const fetchLabels = async () => {
      const isLoggedIn = authedUserId !== null;
      if (!isLoggedIn) {
        setIsCorporateLabelsLoading(false);
        return;
      }

      try {
        reduxStore.dispatch(getCorporateLabels());
      } catch (error: any) {
        logger.error('Error fetching corporate labels:', error);
      } finally {
        setIsCorporateLabelsLoading(false);
      }
    };

    fetchLabels();
  }, [authedUserId]);

  if (!i18nReady || isCorporateLabelsLoading) {
    return (
      <div className={styles.i18nLoader}>
        <CircularLoader size="large" />
      </div>
    );
  }

  const content = (
    <MouseTipRoot>
      <div className={styles.body}>
        <ErrorBoundary>{children}</ErrorBoundary>
      </div>
      <ContextMenuRoot />
      <ModalRoot />
    </MouseTipRoot>
  );

  const getLabelI18nInstance = () => labelI18nInstance;

  return (
    <div className={styles.container}>
      {/* @ts-expect-error - TS2786 - 'Provider' cannot be used as a JSX component. */}
      <Provider store={reduxStore}>
        <FluxRoot store={senseStore}>
          <AgencyConfigContext.Provider value={config || {}}>
            <I18nContext.Provider
              value={{labelI18nInstance: getLabelI18nInstance()}}
            >
              <PageTitleProvider>
                <SenseWindowWidthProvider userAgent={headers['user-agent']}>
                  {/* @ts-expect-error - TS2786 - 'Helmet' cannot be used as a JSX component. */}
                  <Helmet defaultTitle="Sense" titleTemplate="%s | Sense">
                    {isDevOrStage({env}) ? (
                      <link href="/images/favicon-dev.svg" rel="icon" />
                    ) : (
                      <link href="/images/favicon.svg" rel="icon" />
                    )}
                  </Helmet>
                  {process.env.EX_ENV === 'server' ? (
                    content
                  ) : (
                    // @ts-expect-error - TS2786 - 'DndProvider' cannot be used as a JSX component.
                    <DndProvider backend={HTML5Backend}>
                      {content}
                      <DragLayer />
                    </DndProvider>
                  )}
                  <ToastContainer />
                  <GenesisToastContainer />
                </SenseWindowWidthProvider>
              </PageTitleProvider>
            </I18nContext.Provider>
          </AgencyConfigContext.Provider>
        </FluxRoot>
      </Provider>
    </div>
  );
}

function SenseWindowWidthProvider({
  userAgent,
  ...props
}: {
  userAgent: string;
  children: React.ReactNode;
}) {
  return (
    <WindowWidthProvider
      defaultWidth={isMobile(userAgent) ? mobileWidthPx : 1000}
      {...props}
    />
  );
}

// Returns an amount of pixels to scroll that gets bigger the
// closer the mouse is to the top or bottom of the window.

function getScrollByY(clientY: any) {
  const scrollZoneHeight = 100;
  const topTriggerLineY = scrollZoneHeight;
  const bottomTriggerLineY = window.innerHeight - scrollZoneHeight;

  if (clientY < topTriggerLineY) {
    return (15 * (clientY - topTriggerLineY)) / scrollZoneHeight;
  } else if (clientY > bottomTriggerLineY) {
    return (15 * (clientY - bottomTriggerLineY)) / scrollZoneHeight;
  } else {
    return 0;
  }
}

// @ts-expect-error - TS7031 - Binding element 'clientY' implicitly has an 'any' type.
function handleDragOver({clientY}) {
  window.scrollBy(0, getScrollByY(clientY));
}
